import { useSetState } from '@va/util/hooks';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useMemo } from 'react';
import { CalendarDate, CalendarObj, PanelView } from '../types';
import { DATEPICKER_FORMATTER, getCurrentDate, getMonths, getNextDate, getPreviousDate } from '../utils';

type CalendarConfig = {
  minDate?: Dayjs;
  maxDate?: Dayjs;
  initialState?: {
    previous?: Dayjs;
    next?: Dayjs;
  };
  shouldDisablePastDates?: boolean;
};

export const useCalendar = ({ maxDate, minDate, shouldDisablePastDates, initialState }: CalendarConfig) => {
  const initialCalendarState = useMemo(
    () => ({
      previous: initialState?.previous ?? dayjs().startOf('day'),
      next: initialState?.next ?? dayjs().add(1, 'month').endOf('day'),
      year: {
        previous: dayjs().year(),
        next: dayjs().year(),
      },
      months: getMonths(),
    }),
    [initialState],
  );

  const [state, setState] = useSetState(initialCalendarState);
  const [panelState, setPanelState] = useSetState({ previous: PanelView.Calendar, next: PanelView.Calendar });

  const isDateDisabled = useCallback(
    (date: Dayjs) => {
      // VISA-11309 and VISA-11325 and task/VISA-12140: Allow the user to set any datepicker value unless they have a free trial active
      const min = !!minDate && shouldDisablePastDates && date.valueOf() < minDate.valueOf();
      const max = !!maxDate && date.valueOf() > maxDate.valueOf();
      return min || max;
    },
    [minDate, maxDate, shouldDisablePastDates],
  );

  const resetPanelState = useCallback(() => {
    setPanelState({ next: PanelView.Calendar, previous: PanelView.Calendar });
  }, [setPanelState]);

  const updateCalendar = useCallback(
    (startedDate: Dayjs, endedDate: Dayjs) => {
      const diffDuration = dayjs.duration(state.next.diff(state.previous));

      setState({ previous: startedDate, next: endedDate });

      if (diffDuration.months() === 2 || (diffDuration.months() === 1 && diffDuration.days() === 7)) {
        setState((currentState) => ({ next: currentState.next.subtract(1, 'month') }));
      }

      if (endedDate.isSame(dayjs(startedDate), 'month') || endedDate.isBefore(dayjs(startedDate))) {
        setState({ next: startedDate.add(1, 'month') });
      }
    },
    [setState, state],
  );

  const calendar = useMemo<{ previous: CalendarObj; next: CalendarObj }>(() => {
    return {
      previous: {
        date: (): CalendarDate[] => {
          return getPreviousDate(state.previous)
            .concat(getCurrentDate(state.previous))
            .concat(getNextDate(state.previous))
            .map(
              (v) =>
                Object.assign(v, {
                  today: v.isToday(),
                  active: state.previous.month() === v.month(),
                  off: state.previous.month() !== v.month(),
                  disabled: isDateDisabled(v) && !v.isToday(),
                }) satisfies CalendarDate,
            );
        },
        month: state.previous && state.previous.format(DATEPICKER_FORMATTER.month),
        year: state.previous && state.previous.year(),
        years: () => {
          const nextYearStart = Math.floor((state.year.previous + 1) / 12) * 12;
          const yearCount = state.year.previous > nextYearStart ? state.year.previous - nextYearStart + 1 : 12;

          return Array.from({ length: yearCount }, (_, i) => nextYearStart + i);
        },
        onPrevious: () => {
          setState((currentState) => ({ previous: currentState.previous.subtract(1, 'month') }));
        },
        onNext: () => {
          const nextData =
            state.previous.diff(state.next, 'months') === -1 || state.previous.diff(state.next, 'months') === 0
              ? state.next.add(1, 'month')
              : state.next;
          setState((currentState) => ({ previous: currentState.previous.add(1, 'month'), next: nextData }));
        },
        onPreviousYear: () => {
          setState((currentState) => {
            const newPreviousYear = Math.floor((currentState.year.previous - 12) / 12) * 12;

            return {
              year: {
                ...currentState.year,
                previous: Math.max(0, newPreviousYear),
              },
            };
          });
        },
        onNextYear: () => {
          setState((currentState) => {
            const currentYear = dayjs().year();
            const previousStartYear = currentState.year.previous + 24;
            const newPreviousYear = previousStartYear < currentYear ? previousStartYear - 12 : currentYear;

            return {
              year: {
                ...currentState.year,
                previous: newPreviousYear,
              },
            };
          });
        },
        openMonth: () => {
          setPanelState((currentState) => ({
            previous: currentState.previous === PanelView.Month ? PanelView.Calendar : PanelView.Month,
          }));
        },

        setMonth: (key) => {
          setState((currentState) => ({
            previous: state.previous.month(key),
            months: getMonths(currentState.previous.year()),
          }));

          setPanelState({ previous: PanelView.Calendar });
        },
        openYear: () => {
          setPanelState((currentState) => ({
            previous: currentState.previous === PanelView.Year ? PanelView.Calendar : PanelView.Year,
          }));
        },
        setYear: (val) => {
          setState((currentState) => ({
            previous: currentState.previous.year(val),
            months: getMonths(val),
          }));

          setPanelState({ previous: PanelView.Calendar });
        },
      },
      next: {
        date: (): CalendarDate[] => {
          return getPreviousDate(state.next)
            .concat(getCurrentDate(state.next))
            .concat(getNextDate(state.next))
            .map(
              (v) =>
                Object.assign(v, {
                  today: v.isToday(),
                  active: state.next.month() === v.month(),
                  off: state.next.month() !== v.month(),
                  disabled: isDateDisabled(v) && !v.isToday(),
                }) satisfies CalendarDate,
            );
        },
        month: state.next && state.next.format(DATEPICKER_FORMATTER.month),
        year: state.next && state.next.year(),
        years: () => {
          const nextYearStart = Math.floor((state.year.next + 1) / 12) * 12;
          const yearCount = state.year.next > nextYearStart ? state.year.next - nextYearStart + 1 : 12;

          return Array.from({ length: yearCount }, (_, i) => nextYearStart + i);
        },
        onPrevious: () => {
          const previousData =
            state.next.diff(state.previous, 'months') === 1 || state.next.diff(state.previous, 'months') === 0
              ? state.previous.subtract(1, 'month')
              : state.previous;

          setState((currentState) => ({ next: currentState.next.subtract(1, 'month'), previous: previousData }));
        },
        onNext: () => {
          setState((currentState) => ({ next: currentState.next.add(1, 'month') }));
        },
        onPreviousYear: () => {
          setState((currentState) => {
            const newPreviousYear = Math.floor((currentState.year.next - 12) / 12) * 12;

            return {
              year: {
                ...currentState.year,
                next: Math.max(0, newPreviousYear),
              },
            };
          });
        },
        onNextYear: () => {
          setState((currentState) => {
            const currentYear = dayjs().year();
            const nextStartYear = currentState.year.next + 24;
            const newStartYear = nextStartYear < currentYear ? nextStartYear - 12 : currentYear;

            return {
              year: {
                ...currentState.year,
                next: newStartYear,
              },
            };
          });
        },
        openMonth: () => {
          setPanelState((currentState) => ({
            next: currentState.next === PanelView.Month ? PanelView.Calendar : PanelView.Month,
          }));
        },
        setMonth: (key) => {
          setState((currentState) => ({
            next: state.next.month(key),
            months: getMonths(currentState.year.next),
          }));

          setPanelState({ next: PanelView.Calendar });
        },
        openYear: () => {
          setPanelState((currentState) => ({
            next: currentState.next === PanelView.Year ? PanelView.Calendar : PanelView.Year,
          }));
        },
        setYear: (val: number) => {
          setState((currentState) => ({
            next: currentState.next.year(val),
            months: getMonths(val),
          }));

          setPanelState({ next: PanelView.Calendar });
        },
      },
    };
  }, [isDateDisabled, setPanelState, setState, state.next, state.previous, state.year.next, state.year.previous]);

  return { calendar, state, setState, panelState, resetPanelState, updateCalendar };
};
